import { useNarrow } from '../../lib/use-narrow';
import { useWorkspaces } from '../../lib/workspaces';
import { RoutinesSummary } from './routines/RoutinesSummary';
import { TasksSummary } from './tasks/TasksSummary';
import { GoalsSummary } from './goals/GoalsSummary';
import { FilesSummaryCard } from './agents/FilesSummaryCard';
import { AppsSummaryCard } from './agents/AppsSummaryCard';
import { useSummaryTilesReady } from './agents/use-summary-tiles-ready';
import styles from './WorkspaceSummaryRow.module.css';

// The five summary tiles — Tasks · Goals · Routines · Files · Apps — scoped to one Space, at the head of
// its Dashboard tab. THE SAME components Your Agents renders account-wide, given a `workspaceId`, rather
// than a second per-workspace implementation: the two surfaces answering the same question differently
// is exactly the drift the merge of "Agents at work" into Dashboard was meant to end.
//
// Two presentations, one per viewport — the same split Your Agents uses (Figma 15311:197455 ≡ the
// agents-page cards, scoped to a Space):
//   • narrow (<720px) — the compact single-row entry tiles with 12px icon-count badges (`entryRow`),
//     grouped Tasks → Goals → Routines → Files → Apps, matching the phones.
//   • wide — the desktop heading-over-badges tiles, in the SAME order (Figma `15378:117541`),
//     reflowing to fewer columns as the width drops.
//
// Each tile links to its own screen pre-filtered to this Space, so the drill-in never widens the scope
// the reader is already in.
export function WorkspaceSummaryRow({ slug, page }: { slug: string; page: string }) {
  const narrow = useNarrow();
  const workspaces = useWorkspaces();
  const workspace = workspaces.status === 'ready' ? workspaces.workspaces.find((w) => w.slug === slug) : undefined;
  // Above the early return, as hook order demands. It reads only whether the queries are in flight, so
  // the momentary unscoped call before the roster lands cannot put an account-wide COUNT on screen.
  const tilesReady = useSummaryTilesReady(workspace?.id);
  // Until the roster resolves there is no id to scope by, and an unscoped read here would briefly show
  // the ACCOUNT's counts inside a Space — a wrong number is worse than a late one.
  if (!workspace) return null;
  // And the row appears as one complete line, not tile by tile — see `useSummaryTilesReady`.
  if (!tilesReady) return null;

  const scope = { workspaceId: workspace.id, workspaceSlug: workspace.slug, pageName: page } as const;

  if (narrow) {
    return (
      <div className={styles.entryCards}>
        <TasksSummary entryRow {...scope} />
        <GoalsSummary entryRow {...scope} />
        <RoutinesSummary entryRow {...scope} />
        <FilesSummaryCard entryRow {...scope} />
        <AppsSummaryCard entryRow {...scope} />
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <TasksSummary {...scope} />
      <GoalsSummary {...scope} />
      <RoutinesSummary {...scope} />
      <FilesSummaryCard {...scope} />
      <AppsSummaryCard {...scope} />
    </div>
  );
}
